import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import ReportCanvas from '../ReportCanvas';
import Row from '../Common/Row';
import DashboardEditingButtons from '../DashboardEditingButtons';
import DashboardMenu from './DashboardMenu';
import WidgetGallery from '../WidgetGallery';
import WidgetGalleryProvider from '../../contextProviders/WidgetGalleryProvider/WidgetGalleryProvider';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import Loading from '../Loading';
import VariableFiltersSlideOut from '../VariableFiltersSlideOut';

import ReactDOM from 'react-dom';
import ConfigureDashboardGadgetProvider from '../../contextProviders/ConfigureDashboardGadgetProvider';
import StudioBlockEditorProvider from '../../contextProviders/StudioBlockEditorProvider';
import { CANVAS_ID, EXCLUDE_HEIGHT, TOP_BAR_HEIGHT } from '../../constants';
import FilterPlatesProvider from '../../contextProviders/FilterPlatesProvider';
import PERMISSIONS from '../../permissionDefinitions';
import PermissionGates from '../PermissionGates';
import isDashboardTemplate from '../../types/isDashboardTemplate';
import { DASHBOARD_MENU_PORTAL_DIV_ID } from '../../screens/DashboardShow/constants';
import Form from '../../kingpin/forms/Form';
import FormHeader from '../../kingpin/forms/FormHeader';
import FormContent from '../../kingpin/forms/FormContent';
import Tooltip from '../Tooltip';
import GadgetSettingsLinkHandler from './GadgetSettingsLinkHandler';
import PopupEntityAppContext from '../../contextProviders/SplashScreenProviders/MiscProviders/PopupEntityAppProvider/PopupEntityAppContext';
import { UIVersionContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import { V3SelectedAppContext } from '../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';

import EntityAppUsageAlert from './EntityAppUsageAlert';
import DashboardFiltersBar from './DashboardFiltersBar';

const TopBarRow = styled(Row)`
  padding: 0 24px;
  align-items: center;
`;

const DashboardWrapper = styled.div<{
  topBars: number;
}>`
  width: 100%;
  display: flex;
  flex-direction: row;
  overflow-y: auto;
  position: relative;
  height: calc(100vh - ${(props) => TOP_BAR_HEIGHT * props.topBars}px);
  max-height: calc(100vh - ${(props) => TOP_BAR_HEIGHT * props.topBars}px);
  background-color: var(--color-bg-primary);
`;

const DashboardMenuPortal = ({ children }: { children: JSX.Element }) => {
  const domNode = document.getElementById(DASHBOARD_MENU_PORTAL_DIV_ID);
  if (domNode) {
    return ReactDOM.createPortal(children, domNode);
  } else {
    return null;
  }
};

const MENU_PERMISSIONS = [
  PERMISSIONS.CONTENT_ACCESS.SET_ANY_AVAILABLE_TO_USERS_OR_PUBLIC,
  PERMISSIONS.DASHBOARDS.EDIT,
  PERMISSIONS.DASHBOARDS.CREATE,
  PERMISSIONS.DASHBOARDS.DELETE_ANY_DASHBOARD,
  PERMISSIONS.DASHBOARDS.DELETE_CREATED_BY_ME,
];

const AdminStuff = ({
  isEditing,
  startEditing,
  saveDashboardAs,
  createTemplateFrom,
  onDelete,
  setCurrentCanvas,
  canvasMode,
  isTemplate,
  openVariableFiltersSlideOut,
}: {
  isEditing: boolean;
  startEditing: () => void;
  saveDashboardAs: (name: string, labelIds: string[]) => Promise<any>;
  createTemplateFrom: () => Promise<void>;
  saveEditAs: (name: string, labelIds: string[]) => Promise<any>;
  onDelete: () => void;
  setCurrentCanvas: React.Dispatch<React.SetStateAction<Canvas>>;
  canvasMode: CanvasMode;
  isTemplate: boolean;
  openVariableFiltersSlideOut: () => void;
}) => (
  <DashboardMenuPortal>
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
      }}
    >
      {!isEditing && (
        <Row>
          <PermissionGates.HasAny requiredPermissions={MENU_PERMISSIONS}>
            <Tooltip content={'Configure Dashboard'}>
              <DashboardMenu
                startEditing={startEditing}
                saveDashboardAs={saveDashboardAs}
                createTemplateFrom={createTemplateFrom}
                onDelete={onDelete}
                setCurrentCanvas={setCurrentCanvas}
                canvasMode={canvasMode}
                isTemplate={isTemplate}
                openVariableFiltersSlideOut={openVariableFiltersSlideOut}
              />
            </Tooltip>
          </PermissionGates.HasAny>
        </Row>
      )}
    </div>
  </DashboardMenuPortal>
);

const Dashboard = ({
  currentCanvas,
  setCurrentCanvas,
  isEditing,
  onEditSave,
  onEditCancel,
  startEditing,
  saveDashboardAs,
  createTemplateFrom,
  saveEditAs,
  onDelete,
  createAndAddDashboardWidget,
  canvasMode,
  previewCanvasMode,
  isSavingAs,
  isWallboard,
  isMobile,
  dashboardId,
  dashboard,
  isVariableFiltersSlideOutOpen,
  openVariableFiltersSlideOut,
  closeVariableFiltersSlideOut,
  isTargetsApp,
  isEntityApp,
}: {
  currentCanvas: Canvas;
  setCurrentCanvas: React.Dispatch<React.SetStateAction<Canvas>>;
  isEditing: boolean;
  onEditSave: () => void;
  onEditCancel: () => void;
  startEditing: () => void;
  saveDashboardAs: (name: string, labelIds: string[]) => Promise<any>;
  createTemplateFrom: () => Promise<void>;
  saveEditAs: (name: string, labelIds: string[]) => Promise<any>;
  onDelete: () => void;
  createAndAddDashboardWidget: (
    gadget: DashboardGadget,
    card: CanvasCard.Card,
  ) => Promise<void>;
  canvasMode: CanvasMode;
  previewCanvasMode?: CanvasMode;
  isSavingAs: boolean;
  isWallboard?: boolean;
  isMobile: boolean;
  dashboardId: string;
  dashboard: DashboardType;
  isVariableFiltersSlideOutOpen: boolean;
  openVariableFiltersSlideOut: () => void;
  closeVariableFiltersSlideOut: () => void;
  isTargetsApp?: boolean;
  isEntityApp?: boolean;
  isEngagementMode?: boolean;
  isWallboardBuilder?: boolean;
}) => {
  const { isV3 } = useContext(UIVersionContext);
  const v3SelectedAppContext = useContext(V3SelectedAppContext);
  const popupEntityAppContext = useContext(PopupEntityAppContext);

  const topBars = useMemo(() => {
    if (isTargetsApp) {
      return 2;
    }

    if (isEntityApp) {
      if (isV3) {
        // Popup entity app: full-screen overlay, no main nav, 2 TopBarDivs
        if (popupEntityAppContext) {
          return 2;
        }
        // New entity app flows (EntityAppScreen, MappedEntityViewScreen) use
        // sidebar navigation — no additional bars exist in the content area.
        if (v3SelectedAppContext) {
          return 1;
        }
        // Old EntityDetailsShow: main nav + 2 TopBarDivs above the content.
        return 3;
      }

      return 2;
    }

    return 1;
  }, [
    isEntityApp,
    isTargetsApp,
    isV3,
    popupEntityAppContext,
    v3SelectedAppContext,
  ]);

  return (
    <>
      <GadgetSettingsLinkHandler />
      <WidgetGalleryProvider
        startEditing={startEditing}
        currentCanvas={currentCanvas}
        setCurrentCanvas={setCurrentCanvas}
        isDashboard
        createAndAddDashboardWidget={createAndAddDashboardWidget}
      >
        <ConfigureDashboardGadgetProvider>
          <StudioBlockEditorProvider contentType="dashboard">
            <DashboardWrapper id="dashboard" topBars={topBars}>
              <div
                data-testid={`dashboard-${dashboardId}`}
                style={{ flex: 1 }}
                id={`${CANVAS_ID}-${dashboardId}`}
              >
                {isEditing && !isMobile && (
                  <TopBarRow
                    centerAlign
                    spaceBetween
                    style={{ height: TOP_BAR_HEIGHT }}
                    className={`${EXCLUDE_HEIGHT}-${dashboardId}`}
                  >
                    <EntityAppUsageAlert />
                    <div style={{ marginLeft: 'auto' }}>
                      <PermissionGates.Has
                        requiredPermission={PERMISSIONS.DASHBOARDS.CREATE}
                      >
                        <DashboardEditingButtons
                          onSave={onEditSave}
                          onCancel={onEditCancel}
                          saveEditAs={saveEditAs}
                          isEditing={isEditing}
                        />
                      </PermissionGates.Has>
                    </div>
                  </TopBarRow>
                )}

                {!isMobile && (
                  <AdminStuff
                    isEditing={isEditing}
                    saveDashboardAs={saveDashboardAs}
                    createTemplateFrom={createTemplateFrom}
                    saveEditAs={saveEditAs}
                    startEditing={startEditing}
                    onDelete={onDelete}
                    setCurrentCanvas={setCurrentCanvas}
                    canvasMode={canvasMode}
                    openVariableFiltersSlideOut={openVariableFiltersSlideOut}
                    isTemplate={isDashboardTemplate(dashboard)}
                  />
                )}

                {!isWallboard && (
                  <DashboardFiltersBar
                    dashboard={dashboard}
                    currentCanvas={currentCanvas}
                    isEditing={isEditing}
                  />
                )}

                {!isSavingAs && (
                  <ReportCanvas
                    isEditing={isEditing}
                    currentCanvas={currentCanvas}
                    setCurrentCanvas={setCurrentCanvas}
                    previewCanvasMode={previewCanvasMode}
                    startEditing={startEditing}
                  />
                )}

                <WidgetGallery />
                <ModalTransition>
                  {isSavingAs && (
                    <Modal shouldScrollInViewport={false} autoFocus={false}>
                      <Form>
                        <FormHeader title={'Saving as...'} />
                        <FormContent>
                          <div
                            style={{
                              padding: 32,
                              height: '40vh',
                              display: 'flex',
                              flexDirection: 'column',
                              alignContent: 'center',
                            }}
                          >
                            <Loading />
                          </div>
                        </FormContent>
                      </Form>
                    </Modal>
                  )}
                </ModalTransition>
              </div>
              {isDashboardTemplate(dashboard) && (
                <FilterPlatesProvider>
                  <VariableFiltersSlideOut
                    dashboardTemplate={dashboard}
                    isOpen={isVariableFiltersSlideOutOpen}
                    close={closeVariableFiltersSlideOut}
                  />
                </FilterPlatesProvider>
              )}
            </DashboardWrapper>
          </StudioBlockEditorProvider>
        </ConfigureDashboardGadgetProvider>
      </WidgetGalleryProvider>
    </>
  );
};

export default Dashboard;
